import { SIGN_IN_INTRO_COPY, type SignInIntroCopy } from "./signInIntroCopy";

const INTRO_ASSET_BASE = "/assets/images/onboarding/intro";

/** Figma-exported lifestyle backgrounds (figma_asserts → public/assets). */
export const SIGN_IN_INTRO_ASSETS = {
  /** Figma 97034:12760 @3x — H5 full-bleed lifestyle bg */
  intro1Bg: `${INTRO_ASSET_BASE}/intro-1-bg.webp`,
  /** Figma 96344:110675 @3x — PC visual panel lifestyle bg */
  intro1BgDesktop: `${INTRO_ASSET_BASE}/intro-1-bg-desktop.webp`,
  /** Figma 97034:12766 @3x — H5 full-bleed Intro 2 bg */
  intro2Bg: `${INTRO_ASSET_BASE}/intro-2-bg.webp`,
  /** Figma 99095:24183 @3x — PC visual panel Intro 2 bg */
  intro2BgDesktop: `${INTRO_ASSET_BASE}/intro-2-bg-desktop.webp`,
  intro3Bg: `${INTRO_ASSET_BASE}/intro-3-bg.webp`,
  intro4Bg: `${INTRO_ASSET_BASE}/intro-4-bg.webp`,
  /** Figma 99095:37559 @3x — PC visual panel lifestyle bg */
  intro4BgDesktop: `${INTRO_ASSET_BASE}/intro-4-bg-desktop.webp`,
  domainCash: `${INTRO_ASSET_BASE}/intro-2-domain-cash.svg`,
  domainInvesting: `${INTRO_ASSET_BASE}/intro-2-domain-investing.svg`,
  domainDecisions: `${INTRO_ASSET_BASE}/intro-2-domain-decisions.svg`,
  domainWealth: `${INTRO_ASSET_BASE}/intro-2-domain-wealth.svg`,
  edCenter: `${INTRO_ASSET_BASE}/intro-2-ed-center.webp`,
  bubbleTail: `${INTRO_ASSET_BASE}/intro-bubble-tail.svg`,
  edAvatar: `${INTRO_ASSET_BASE}/intro-ed-avatar.webp`,
  edLogo: "/assets/images/ed-circle-logo-v2.svg",
  brandLogo: `${INTRO_ASSET_BASE}/intro-brand-logo.svg`,
  /** Figma gift 102803:30122 — Free chip；iconfont 生成后可改 Icon name="gift" */
  giftIcon: `${INTRO_ASSET_BASE}/intro-gift.svg`,
  goalShield: `${INTRO_ASSET_BASE}/intro-3-goal-shield.svg`,
  goalDollarBag: `${INTRO_ASSET_BASE}/intro-3-goal-dollar-bag.svg`,
  goalPlant: `${INTRO_ASSET_BASE}/intro-3-goal-plant.svg`,
} as const;

/** Figma Intro 3 segment fill ~4.98s — keep carousel/segment in sync at 5s. */
export const SIGN_IN_INTRO_CAROUSEL_INTERVAL_MS = 5000;

export const INTRO_SLIDE_COUNT = 4;

export interface IntroGoalCardDemo {
  id: string;
  title: string;
  percent: number;
  amountLabel: string;
  icon: string;
  /** Figma stagger offset — mobile (96293:45206) */
  mobileOffsetClassName: string;
  /** Figma stagger offset — desktop (96344:109912) */
  desktopOffsetClassName: string;
}

/** Pilot demo amounts (D16) — Figma 96293:47269 / 96344:109882; not i18n. */
export function getIntro3GoalCards(copy: SignInIntroCopy): IntroGoalCardDemo[] {
  return [
    {
      id: "safety-net",
      title: copy.intro3.goals.safetyNet,
      percent: 40,
      amountLabel: "$12k of $30k",
      icon: SIGN_IN_INTRO_ASSETS.goalShield,
      mobileOffsetClassName: "ml-6",
      desktopOffsetClassName: "md:ml-8",
    },
    {
      id: "down-payment",
      title: copy.intro3.goals.downPayment,
      percent: 20,
      amountLabel: "$40k of $200k",
      icon: SIGN_IN_INTRO_ASSETS.goalDollarBag,
      mobileOffsetClassName: "",
      desktopOffsetClassName: "",
    },
    {
      id: "retirement",
      title: copy.intro3.goals.retirement,
      percent: 60,
      amountLabel: "$18k of $30k",
      icon: SIGN_IN_INTRO_ASSETS.goalPlant,
      mobileOffsetClassName: "ml-4",
      desktopOffsetClassName: "md:ml-[22px]",
    },
  ];
}

export const INTRO3_GOAL_CARDS: IntroGoalCardDemo[] =
  getIntro3GoalCards(SIGN_IN_INTRO_COPY);

export interface IntroSlideTheme {
  /** CSS background when image missing */
  fallbackClassName: string;
  backgroundImage?: string;
  /** Next/Image class when backgroundImage is set (H5 full-bleed) */
  backgroundImageClassName?: string;
  /** Optional H5 background wrapper positioning (Figma absolute frame) */
  backgroundImageWrapperClassName?: string;
  /** Optional PC visual-panel bg (falls back to backgroundImage) */
  desktopBackgroundImage?: string;
  /** Next/Image class for desktop visual panel (no scrim overlay) */
  desktopBackgroundImageClassName?: string;
  /** Single scrim layer (used when scrimLayers is empty) */
  scrimClassName: string;
  /** Optional stacked scrims (Figma Intro 1 uses two layers) */
  scrimLayers?: string[];
  /** PC visual panel scrims (Intro 2 green gradients) */
  desktopScrimLayers?: string[];
  /** PC shell / left panel background (Intro 3 teal #036263) */
  desktopShellClassName?: string;
}

export const INTRO_SLIDE_THEMES: IntroSlideTheme[] = [
  {
    fallbackClassName: "bg-content_brand_primary",
    backgroundImage: SIGN_IN_INTRO_ASSETS.intro1Bg,
    /** Figma 97034:12760 export is already the framed crop — full-bleed cover */
    backgroundImageWrapperClassName: "absolute inset-0",
    backgroundImageClassName: "object-cover object-center",
    desktopBackgroundImage: SIGN_IN_INTRO_ASSETS.intro1BgDesktop,
    desktopBackgroundImageClassName: "object-cover object-center",
    scrimClassName: "",
    scrimLayers: [
      "bg-gradient-to-b from-[rgba(28,18,8,0)] from-[60%] via-[86%] via-[rgba(28,18,8,0.34)] to-[rgba(28,18,8,0.6)] opacity-50",
      "bg-gradient-to-b from-[rgba(28,18,8,0.55)] to-[55%] to-[rgba(28,18,8,0)]",
    ],
  },
  {
    fallbackClassName: "bg-[#2b9146]",
    backgroundImage: SIGN_IN_INTRO_ASSETS.intro2Bg,
    backgroundImageClassName: "object-cover object-center",
    desktopBackgroundImage: SIGN_IN_INTRO_ASSETS.intro2BgDesktop,
    desktopBackgroundImageClassName: "object-cover object-center",
    scrimClassName: "",
    scrimLayers: [
      "bg-gradient-to-b from-[rgba(31,102,49,0.13)] to-1/4 to-[rgba(31,102,49,0)]",
      "bg-gradient-to-b from-[rgba(31,102,49,0.6)] via-[30%] via-[rgba(31,102,49,0.2)] to-[46%] to-[rgba(31,102,49,0)] mix-blend-darken",
      "bg-gradient-to-b from-[rgba(31,102,49,0)] from-[60%] via-[86%] via-[rgba(31,102,49,0.34)] to-[rgba(31,102,49,0.6)] opacity-50",
    ],
    desktopScrimLayers: [
      "bg-[#2b9146] mix-blend-darken opacity-50",
      "bg-gradient-to-b from-[rgba(31,102,49,0.13)] to-1/4 to-[rgba(31,102,49,0)]",
      "bg-gradient-to-b from-[rgba(31,102,49,0.6)] via-[30%] via-[rgba(31,102,49,0.2)] to-[46%] to-[rgba(31,102,49,0)] mix-blend-darken",
      "bg-gradient-to-b from-[rgba(31,102,49,0)] from-[60%] via-[86%] via-[rgba(31,102,49,0.34)] to-[rgba(31,102,49,0.6)] opacity-50",
    ],
  },
  {
    fallbackClassName: "bg-[#d97757]",
    backgroundImage: SIGN_IN_INTRO_ASSETS.intro3Bg,
    backgroundImageWrapperClassName:
      "absolute -left-[74%] top-0 h-full w-[248%]",
    backgroundImageClassName: "object-cover object-center",
    desktopBackgroundImageClassName: "object-cover object-center",
    desktopShellClassName: "md:bg-[#036263]",
    scrimClassName: "",
    scrimLayers: [
      "bg-gradient-to-b from-[rgba(28,18,8,0.13)] to-1/4 to-[rgba(28,18,8,0)]",
      "bg-gradient-to-b from-[rgba(85,41,27,0.6)] via-[30%] via-[rgba(85,41,27,0.2)] to-[46%] to-[rgba(85,41,27,0)]",
      "bg-gradient-to-b from-[rgba(28,18,8,0)] from-[60%] via-[86%] via-[rgba(28,18,8,0.34)] to-[rgba(28,18,8,0.6)] opacity-50",
      "bg-[#d97757] mix-blend-hard-light opacity-60",
      "bg-[#d97757] mix-blend-overlay opacity-60",
    ],
    desktopScrimLayers: [
      "bg-gradient-to-b from-[rgba(28,18,8,0.13)] to-1/4 to-[rgba(28,18,8,0)]",
      "bg-gradient-to-b from-[rgba(85,41,27,0.6)] via-[30%] via-[rgba(85,41,27,0.2)] to-[46%] to-[rgba(85,41,27,0)]",
      "bg-gradient-to-b from-[rgba(28,18,8,0)] from-[60%] via-[86%] via-[rgba(28,18,8,0.34)] to-[rgba(28,18,8,0.6)] opacity-50",
      "bg-[#d97757] mix-blend-hard-light opacity-60",
      "bg-[#d97757] mix-blend-overlay opacity-60",
    ],
  },
  {
    fallbackClassName: "bg-content_brand_primary",
    /** Figma 97034:12742 @3x — H5 full-bleed */
    backgroundImage: SIGN_IN_INTRO_ASSETS.intro4Bg,
    backgroundImageWrapperClassName:
      "absolute -left-[74%] top-0 h-full w-[248%]",
    backgroundImageClassName: "object-cover object-center",
    desktopBackgroundImage: SIGN_IN_INTRO_ASSETS.intro4BgDesktop,
    desktopBackgroundImageClassName: "object-cover object-center",
    desktopShellClassName: "md:bg-content_brand_primary",
    scrimClassName: "",
    scrimLayers: [
      "bg-gradient-to-b from-[rgba(3,33,36,0.4)] to-1/4 to-[rgba(3,33,36,0)]",
      "bg-gradient-to-b from-[rgba(3,33,36,0)] from-[60%] via-[86%] via-[rgba(3,33,36,0.34)] to-[rgba(3,33,36,0.6)] opacity-50",
      "bg-gradient-to-b from-[rgba(3,38,41,0.6)] via-[30%] via-[rgba(3,38,41,0.2)] to-[46%] to-[rgba(3,38,41,0)]",
    ],
    desktopScrimLayers: [
      "bg-gradient-to-b from-[rgba(3,33,36,0.4)] to-1/4 to-[rgba(3,33,36,0)]",
      "bg-gradient-to-b from-[rgba(3,33,36,0)] from-[60%] via-[86%] via-[rgba(3,33,36,0.34)] to-[rgba(3,33,36,0.6)] opacity-50",
      "bg-gradient-to-b from-[rgba(3,38,41,0.6)] via-[30%] via-[rgba(3,38,41,0.2)] to-[46%] to-[rgba(3,38,41,0)]",
    ],
  },
];
